/**
 * Org-scoped URL support (/puma/productions — PLAN_MULTI_ORG_ADMIN.md §4.1).
 *
 * The org-admin shell is mounted both at /organization-admin/* (plain org admins,
 * org implicit) and at /:orgSlug/* (multi_org_admin + super_admin browsing a
 * specific org). Internal links/comparisons are written against the legacy
 * /organization-admin prefix and passed through orgPath(), which swaps the
 * prefix for the current slug base when the page is served from a slug mount.
 */

/** First URL segments that are fixed app mounts — everything else is an org slug. */
const FIXED_PREFIXES = new Set([
  'admin', 'organization-admin', 'client-admin', 'agency', 'scout', 'model',
  'login', 'logout', 'forgot-password', 'reset-password', 'unauthorized',
  'model-book', 'casting', 'confirmation', 'request', 'complete-model',
  'complete-agency', 'model-update', 'api',
]);

/** Base path of the org-admin shell for the current URL: '/organization-admin' or '/<slug>'. */
export function orgBasePath(): string {
  const seg = window.location.pathname.split('/')[1] || '';
  return seg && !FIXED_PREFIXES.has(seg) ? `/${seg}` : '/organization-admin';
}

/** True when the app is currently served from an org-slug mount (/puma/...). */
export function isOrgSlugMount(): boolean {
  return orgBasePath() !== '/organization-admin';
}

/**
 * Rewrites a legacy '/organization-admin/...' path to the current mount's base.
 * Under /organization-admin this is the identity, so existing behavior is unchanged.
 */
export function orgPath(path: string): string {
  const base = orgBasePath();
  if (base === '/organization-admin') return path;
  return path.startsWith('/organization-admin')
    ? base + path.slice('/organization-admin'.length)
    : path;
}

/**
 * Base path of the Users section for the mount the page is currently served from.
 *
 * The same four pages are mounted under three shells — /admin/users/* (super admin),
 * /all/users/* (multi-org admin) and /<slug>|/organization-admin/users/* (org admin) — so a
 * hard-coded '/admin/users' link inside them sends a multi-org admin to a route
 * ProtectedRoute refuses. 'all' is not a FIXED_PREFIX, so orgPath() already rewrites the
 * legacy prefix to '/all' under the multi-org mount; this only adds the /admin case.
 */
export function usersBasePath(): string {
  return inAdminMount() ? '/admin/users' : orgPath('/organization-admin/users');
}

/** True when the current page is served from the super-admin mount (/admin/...). */
export function inAdminMount(): boolean {
  const p = window.location.pathname;
  return p === '/admin' || p.startsWith('/admin/');
}

/**
 * True when this user acts as an ORG ADMIN in the current context: real org
 * admins always; multi-org/super admins only while browsing an org (/<slug>/…
 * or /all — their requests carry X-Organization-Id and morph server-side).
 * Never true for pool_admin/agency/etc., so shared components stay safe.
 *
 * multi_org_viewer/finance_admin are included: they browse the org shell exactly
 * like multi_org_admin, so they need the same routing and org switching. What
 * they may *change* there is a separate question — see lib/permissions.ts.
 */
export function actsAsOrgAdmin(role?: string): boolean {
  return (
    role === 'organization_admin' ||
    ((role === 'multi_org_admin' ||
      role === 'multi_org_viewer' ||
      role === 'finance_admin' ||
      role === 'super_admin' ||
      role === 'admin') &&
      !inAdminMount())
  );
}
